import { averageScore } from '@/lib/scoreStatistics';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Target, TrendingUp, Trophy, Users } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface GoalTrackingWidgetProps {
  companyId?: string | null;
}

interface GoalProgress {
  label: string;
  current: number | null;
  target: number;
  icon: React.ElementType;
  color: string;
}

export function GoalTrackingWidget({ companyId }: GoalTrackingWidgetProps) {
  const { data: goals = [], isLoading } = useQuery({
    queryKey: ['goal-tracking', companyId],
    queryFn: async () => {
      // Get current month's start
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);

      let evaluationsQuery = supabase
        .from('simulation_evaluations')
        .select('overall_score, user_id, agent_id')
        .gte('created_at', monthStart.toISOString())
        .eq('is_submitted', true)
        .is('deleted_at', null);

      if (companyId) {
        evaluationsQuery = evaluationsQuery.eq('company_id', companyId);
      }

      const { data: evaluations } = await evaluationsQuery;

      // Get agents with goal scores
      let agentsQuery = supabase
        .from('simulation_agents')
        .select('id, goal_score')
        .eq('status', 'active');

      if (companyId) {
        agentsQuery = agentsQuery.or(`company_id.eq.${companyId},company_id.is.null`);
      }

      const { data: agents } = await agentsQuery;

      // Calculate metrics
      const totalSessions = evaluations?.length || 0;
      const uniqueUsers = new Set(evaluations?.map(e => e.user_id) || []).size;
      const avgScore = averageScore(evaluations);

      // Count sessions that met goal
      const goalsAchieved = evaluations?.filter(e => {
        const agent = agents?.find(a => a.id === e.agent_id);
        return e.overall_score && agent?.goal_score && e.overall_score >= agent.goal_score;
      }).length || 0;

      // Set targets (these could be configurable per company in future)
      const monthlySessionTarget = companyId ? 50 : 500;
      const monthlyUserTarget = companyId ? 10 : 100;
      const scoreTarget = 75;
      const goalAchievementTarget = Math.round(totalSessions * 0.7) || 10;

      return [
        {
          label: 'Monthly Sessions',
          current: totalSessions,
          target: monthlySessionTarget,
          icon: Target,
          color: 'bg-primary',
        },
        {
          label: 'Active Users',
          current: uniqueUsers,
          target: monthlyUserTarget,
          icon: Users,
          color: 'bg-emerald-500',
        },
        {
          label: 'Avg Score Target',
          current: avgScore,
          target: scoreTarget,
          icon: TrendingUp,
          color: 'bg-blue-500',
        },
        {
          label: 'Goals Achieved',
          current: goalsAchieved,
          target: goalAchievementTarget,
          icon: Trophy,
          color: 'bg-amber-500',
        },
      ] as GoalProgress[];
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Goal Tracking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Goal Tracking
        </CardTitle>
        <CardDescription>Monthly progress towards targets</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {goals.map((goal) => {
          const percentage = Math.min(Math.round(((goal.current ?? 0) / goal.target) * 100), 100);
          const isAchieved = goal.current !== null && goal.current >= goal.target;

          return (
            <div key={goal.label} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <goal.icon className={`h-4 w-4 ${isAchieved ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                  <span className="text-sm font-medium">{goal.label}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {goal.current === null ? 'Not scored' : `${goal.current} / ${goal.target}`}
                  {isAchieved && <span className="ml-2 text-emerald-500">✓</span>}
                </span>
              </div>
              <Progress 
                value={percentage} 
                className="h-2"
              />
              <p className="text-xs text-muted-foreground text-right">
                {goal.current === null ? 'No scored sessions yet' : `${percentage}% complete`}
              </p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
